/* The Showroom.
 *
 * Every card is rendered by Liquid — the shop's own card, from snippets/showroom-card.liquid, with
 * its carousel, review tab, price badge, size toggle and quantity row all behaving as they always
 * have (showroom-v3.js owns that). This file never builds a card. It shows and hides them, and it
 * runs the bar, the strip, the rail and the cart mode around them.
 *
 * URL state is the API: scope, search, every chip, the page and the open product live in the
 * address bar, so a view can be shared and comes back exactly as it was.
 */
(() => {
  const app = document.getElementById('sr-app');
  if (!app) return;

  const PER = +app.dataset.perPage || 24;
  /* Two different free things (2026-09-25): local delivery in Calgary over FREE_AT, and shipping
     everywhere else over FREE_SHIP (0 = none). Which one a basket is chasing depends on the zone. */
  const FREE_AT = +app.dataset.freeDeliveryAt || 60;
  const FREE_SHIP = +app.dataset.freeShippingAt || 0;
  const MIN_ORDER = +app.dataset.minimumOrder || 0;
  /* What the rail opens on before anyone has picked anything. An empty rail is a wasted first
     impression — a third of the screen asking for a click instead of showing one tea. */
  const OPENING = (app.dataset.opening || '').trim();
  /* WHICH FACETS THIS PAGE OFFERS. Empty on the Showroom, where every dimension applies. A
     collection page names its own: a shelf of black teas asks about character and caffeine and
     nothing else, because "Kind" has one answer there and the spice and wellness questions are
     about a different wall. Keys as in FACETS; unknown keys are ignored. */
  const ONLY = (app.dataset.facets || '').split(',').map((s) => s.trim()).filter(Boolean);
  /* What the strip calls the cards. Empty on the Showroom, where the scope decides — "teas",
     "spices", "teas and spices". A collection page names its own, because "28 teas and spices" over
     a shelf of black teas is wrong in both halves. */
  const NOUN = (app.dataset.noun || '').trim();
  const $ = (id) => document.getElementById(id);
  const money = (n) => '$' + n.toFixed(2);
  /* The zone decides the finish line: Calgary chases free local delivery, everyone else free
     shipping. The zone records call Calgary 'local-delivery' / "Calgary Area". */
  const isLocal = (z) => !!z && /local|calgary/i.test(`${z.handle} ${z.label}`);
  const shipsFree = (z) => FREE_SHIP > 0 && !isLocal(z) && cart.total >= FREE_SHIP;
  /* What the zone costs for THIS basket, in cents. Calgary is free over FREE_AT and pays the Alberta
     rate under it (Dayton, 2026-09-25: "Under $60 the Calgary area is charged the same as the rest
     of Alberta") — its record says 0 because the free case is the one it is named for. */
  const albertaCents = () => (ZONES.find((x) => x.handle === 'alberta' || /^alberta$/i.test(x.label)) || {}).price || 0;
  const zoneCents = (z) => {
    if (isLocal(z)) return cart.total >= FREE_AT ? 0 : (albertaCents() || z.price);
    return shipsFree(z) ? 0 : z.price;
  };
  /* THE BAR'S THREE TICKS (Dayton, 2026-09-25): the minimum, free local delivery in Calgary, free
     shipping across Canada. The track runs to the furthest; the Calgary tick only shows as a tick
     when free shipping is on (otherwise it is the end of the track). The cart drawer
     (snippets/cart-drawer-buybox.liquid) and the homepage band draw the same bar with the same words. */
  const SCALE = Math.max(MIN_ORDER, FREE_AT, FREE_SHIP) || 1;
  const LOCAL_TICK = FREE_SHIP > 0 && FREE_AT > 0 && FREE_AT < SCALE;
  const m0 = (n) => '$' + (Number.isInteger(n) ? n : n.toFixed(2));
  const toGo = (now, b) => {
    const toMin = MIN_ORDER - now, toLocal = FREE_AT - now, toShip = FREE_SHIP - now;
    const B = (x) => `<${b}>${money(x)}</${b}>`;
    if (toMin > 0) return `${B(toMin)} more to reach the ${m0(MIN_ORDER)} minimum order`;
    if (!FREE_SHIP) return toLocal > 0 ? `${B(toLocal)} more for free local delivery in Calgary` : 'Free local delivery in Calgary — you’re there.';
    if (toLocal > 0) return `${B(toLocal)} more for free delivery in Calgary, ${B(toShip)} for free shipping across Canada`;
    if (toShip > 0) return `${B(toShip)} more for free shipping across Canada — Calgary delivery is already free`;
    return 'Free shipping across Canada — you’re there.';
  };
  const nice = (v) => String(v).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* ---------- the shop's own words ----------
     assets/showroom-labels.js is generated from snippets/showroom-filter-bar.liquid, which is
     where this shop's filter vocabulary has always lived. Before this, the showroom prettified the
     raw metafield handles instead, and invented its own wording in a hand-kept map beside it. That
     was wrong in three ways at once: the wellness filters lost the QUESTIONS they were written as
     ("I'm stuffed up" became "Stuffed up", "I can't sleep" became "Sleep"); two different answers
     collapsed into one word, because the map was keyed on the leaf and not the pair, so
     "Anxious / Overwhelmed" under I can't calm down and "Anxious / On edge" under I'm feeling off
     both printed as "Anxious"; and spices read as handles — "Chiles Peppers" where the shop says
     "Chiles". nice() stays as the fallback for any value the bar has no word for. */
  const L = window.SR_LABELS || { q: {}, v: {} };
  /* facet key in this file -> the dimension the filter bar files it under */
  const DIM = { st: 'spice_type', cu: 'spice_cuisine', di: 'spice_dish', pr: 'spice_protein', he: 'spice_heat',
    tt: 'tea_type', ch: 'tea_character', en: 'tea_energy' };
  /* THE SPICE WALL'S OWN SHELVES (Dayton, 2026-10-31: "the typical categories first like baking spices, dry
     rubs, chile flakes and chilies, garlic & onion, sea salts, peppercorns, specialty blends, etc. Cuisine,
     dish, goes on, aren't default categories for spices"). custom.spice_type, in his order and in his words
     — the filter bar's are older and shorter ("Chiles", "Garlic/Onion") and it files baking under `baking`
     where the metafield says `baking-spices`. The three he did not name follow, largest first. */
  const ST = [['baking-spices', 'Baking Spices'], ['dry-rubs', 'Dry Rubs'], ['chiles-peppers', 'Chile Flakes & Chilies'],
    ['garlic-onion', 'Garlic & Onion'], ['salt-finishing', 'Sea Salts'], ['peppercorns', 'Peppercorns'],
    ['specialty-blends', 'Specialty Blends'], ['herbs', 'Herbs'], ['single-spices', 'Single Spices'],
    ['curry-blends', 'Curry Blends']];
  const ST_LABEL = Object.fromEntries(ST);
  const ST_ORDER = ST.map(([v]) => v);
  function facetLabel(k, v) {
    if (k === 'st' && ST_LABEL[v]) return ST_LABEL[v];
    if (k === 'jo') return L.q[v] || nice(v);
    /* keyed on the WHOLE pair, because the same leaf means different things under different
       questions and the shop wrote different words for each */
    if (k === 'an') return L.v['benefit:' + v] || nice(v.split('|')[1] || v);
    return L.v[DIM[k] + ':' + v] || nice(v);
  }
  const pretty = (v) => nice(v);
  /* Where a value sits in the filter bar's own listing. Anything the bar does not name sorts to
     the end rather than to the front, so a stray metafield value cannot lead the questions. */
  const Q_ORDER = Object.keys(L.q);
  const V_ORDER = Object.keys(L.v);
  function order(k, v) {
    const i = k === 'jo' ? Q_ORDER.indexOf(v) : k === 'st' ? ST_ORDER.indexOf(v) : V_ORDER.indexOf('benefit:' + v);
    return i < 0 ? 999 : i;
  }

  const FACETS = [
    { k: 'st', g: 'Spice', label: 'Kind' },
    { k: 'cu', g: 'Spice', label: 'Cuisine' }, { k: 'di', g: 'Spice', label: 'Dish' },
    { k: 'pr', g: 'Spice', label: 'Goes on' }, { k: 'he', g: 'Spice', label: 'Heat' },
    { k: 'tt', g: 'Tea', label: 'Kind' }, { k: 'ch', g: 'Tea', label: 'Character' },
    { k: 'en', g: 'Tea', label: 'Caffeine' },
    { k: 'jo', g: 'Wellness', label: 'What’s going on?' },
    { k: 'an', g: 'Wellness', label: 'More precisely' },
  ];
  const GROUPS = [
    { name: 'Tea', hint: 'what to drink', scope: 't' },
    { name: 'Spice', hint: 'what to cook with', scope: 's' },
    { name: 'Wellness', hint: 'what you need it to do', scope: '' }, /* a need spans both walls */
  ];

  /* ---------- what GA4 gets told ----------
     Shopify loads GA4 inside its sandboxed Web Pixel, so theme code cannot call gtag; the
     supported road is Shopify.analytics.publish(), which a custom pixel (docs/analytics/
     ga4-custom-pixel.js) subscribes to and forwards. Until 2026-09-20 this engine published
     nothing, so GA4 saw one page view per shelf and none of what happened on it — which filter
     gets used, which card gets opened, whether anyone flips to the cart.

     Guarded, because the global only exists once the pixel manager has booted, and a shelf must
     never fail to filter because analytics was late. Every payload carries `surface` — the
     collection handle, or the page path — so one event name reads the same way from the
     Showroom, a collection shelf and the delivery page's shelf. Read these as RELATIVE, never
     absolute: the cookie banner enforces consent for Canadian visitors, and a decline suppresses
     every one of them. */
  const SURFACE = (location.pathname.replace(/^\/collections\//, '').replace(/^\//, '').replace(/\//g, ':').replace(/:$/, '')) || 'showroom';
  const track = (name, payload) => { try { window.Shopify?.analytics?.publish?.(name, { surface: SURFACE, ...payload }); } catch (_) { /* analytics never breaks a shelf */ } };

  const S = { scope: 'all', q: '', page: 1, sel: null, view: 'grid', f: {}, zone: null, selIsDefault: false };
  FACETS.forEach((f) => (S.f[f.k] = new Set()));
  let ZONES = [];
  let cart = { items: [], total: 0 };
  const PAY = document.getElementById('sr-pay')?.innerHTML || '';

  /* ---------- the phone ----------
     809px is where the rail actually runs out of room — two columns and the rail together stop
     fitting, so the rail goes and the command bar takes over. Above it the rail is present at every
     width, the grid simply gives up columns beside it (4 / 3 / 2 across 1270 and 1050).
     This is the ONE number that decides whether the product detail, the filters and the buy box
     live in the rail or in a sheet; it has to be the same number the stylesheet drops the rail on
     or a width exists where they both think the other is handling it. */
  const MOBILE = window.matchMedia('(max-width: 809px)');
  const isMobile = () => MOBILE.matches;

  /* ---------- the filters rail ----------
     On a shelf that asks for it (the Showroom's "Filters in a left rail"), the filters panel lives
     in a rail against the browser's LEFT edge from 1200px up, the way FMF's Showroom has them
     (Dayton, 2026-10-31: "much cleaner and makes them always accessible"; then "sprouting from the
     left browser edge", with at least three columns of cards). Below 1200px the same panel opens in
     the sheet, from the bar's Filters button or the phone's tab. 1200 is where three columns of cards
     still fit beside two rails (179px; 205 at 1280); the stylesheet draws the rail on the same
     number. Off, the panel is the bar's dropdown, as on every collection shelf. */
  const FRAIL = app.classList.contains('sr--frail');
  const WIDE = window.matchMedia('(min-width: 1200px)');
  const FRAIL_HIDDEN_KEY = 'sr-filters-hidden';

  /* ---------- recently viewed ----------
     Per device, in localStorage, and never sent anywhere. The desktop rail persists, so recents
     there is a convenience; on a phone the detail is a sheet that closes, so every product looked
     at vanishes on dismiss and this is the only trail back to it. Handles only — the titles,
     pictures and prices are read off the cards already on the page, so the store cannot go stale
     against a renamed or repriced product. */
  const RECENTS_KEY = 'sr-recents';
  const RECENTS_MAX = 12;
  let RECENTS = [];
  try { RECENTS = (JSON.parse(localStorage.getItem(RECENTS_KEY)) || []).filter((h) => typeof h === 'string'); } catch (_) {}
  function remember(handle) {
    if (!handle || !BY[handle]) return;
    RECENTS = [handle, ...RECENTS.filter((h) => h !== handle)].slice(0, RECENTS_MAX);
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(RECENTS)); } catch (_) {}
    paintRecents();
  }
  /* Handles whose card is still on the page — a product pulled from the shop should not leave a
     row that opens nothing. */
  const recentCards = () => RECENTS.map((h) => BY[h]).filter(Boolean);

  /* read every card once — after this, filtering touches no attributes */
  const CARDS = [...document.querySelectorAll('#sr-grid .product-card')].map((el) => {
    const d = el.dataset;
    const split = (v) => (v || '').split('|').map((x) => x.trim()).filter(Boolean);
    /* `an` alone is ';'-separated, because each of its values contains a '|' of its own —
       "stomach|burning" is one answer, not two. See the note on data-an in showroom-card.liquid. */
    const pairs = (v) => (v || '').split(';').map((x) => x.trim()).filter((x) => x.includes('|'));
    const f = { st: split(d.st), cu: split(d.cu), di: split(d.di), pr: split(d.pr), he: split(d.he),
      tt: split(d.tt), ch: split(d.ch), en: split(d.en), jo: split(d.jo), an: pairs(d.an) };
    /* The haystack. It used to be the title and the subtitle alone, which meant a search for
       "dry rub" or "sea salt" or "grilled" found nothing at all unless the words happened to be in
       a product's name. Everything the card already knows goes in: the type and tags from Liquid,
       every facet value, and the shop's OWN WORDS for those values out of the filter bar — so
       "Cajun & Creole" is findable by typing cajun even though the metafield says
       cajun-creole. Hyphens become spaces so a handle matches the words inside it. */
    const words = [d.title, d.sub, d.terms];
    Object.keys(f).forEach((k) => f[k].forEach((v) => {
      words.push(v.replace(/[|-]/g, ' '));
      words.push(facetLabel(k, v));
    }));
    return {
      el, h: d.productHandle, kind: d.kind,
      /* In the cart but not of this shelf (showroom-shell.liquid's guest loop). Shown in cart
         view, opened in the rail, given its in-cart tab — and never part of the grid, the counts
         or the facets, because it is not something this page offers. */
      guest: 'srGuest' in d,
      text: words.join(' ').toLowerCase(),
      f,
    };
  });
  const BY = Object.fromEntries(CARDS.map((c) => [c.h, c]));
  /* The shelf proper — what the page offers — for every count that describes it. */
  const SHELF = CARDS.filter((c) => !c.guest);

  /* ---------- url ---------- */
  /* Two Shopify-reserved names are deliberately NOT used here.
     `view` selects an alternate template — ?view=cart sent Shopify looking for
     collection.showroom.cart, and finding nothing it fell back to the default collection page.
     `page` drives Liquid's own paginate; ours is client-side, so ?page=2 would have asked the
     server for a second page of a collection that only ever renders one.
     Which mode the app is in is not in the URL at all: nobody shares their cart view, and the
     cart itself lives on Shopify's side, so the state was carrying no weight. */
  function readURL() {
    const u = new URLSearchParams(location.search);
    S.scope = u.get('scope') || 'all';
    S.q = u.get('q') || '';
    S.page = Math.max(1, +(u.get('sr_page') || 1));
    S.sel = u.get('p') || null;
    /* Nobody asked for the opening product, so it does not go back into the address bar —
       otherwise every visitor's first copied link would carry a tea they never chose. */
    if (!S.sel && OPENING && BY[OPENING]) { S.sel = OPENING; S.selIsDefault = true; }
    else S.selIsDefault = false;
    S.view = 'grid';
    FACETS.forEach((f) => (S.f[f.k] = new Set((u.get(f.k) || '').split(',').filter(Boolean))));
    /* With the rail there is no wall picker (see showroom-shell.liquid), one chip a facet and one group's
       chips at a time (the chip handler), so an old link's ?scope=t opens the Tea group instead of quietly
       hiding everything else with nothing on screen to undo it, a facet that arrives with two values keeps
       the first, and chips from two groups keep the first group's. */
    if (FRAIL) {
      const wall = { t: 'Tea', s: 'Spice', w: 'Wellness' }[S.scope];
      if (wall) PICK = wall;
      S.scope = 'all';
      FACETS.forEach((f) => { if (S.f[f.k].size > 1) S.f[f.k] = new Set([[...S.f[f.k]][0]]); });
      const keep = GROUPS.find((g) => FACETS.some((f) => f.g === g.name && S.f[f.k].size));
      if (keep) FACETS.forEach((f) => { if (f.g !== keep.name) S.f[f.k].clear(); });
    }
  }
  function writeURL() {
    const u = new URLSearchParams();
    if (S.scope !== 'all') u.set('scope', S.scope);
    if (S.q) u.set('q', S.q);
    if (S.page > 1) u.set('sr_page', S.page);
    if (S.sel && !S.selIsDefault) u.set('p', S.sel);
    FACETS.forEach((f) => { if (S.f[f.k].size) u.set(f.k, [...S.f[f.k]].join(',')); });
    const qs = u.toString();
    history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
  }

  /* ---------- filtering ---------- */
  /* The words the shop and its customers spell differently. Deliberately SHORT and hand-kept: a
     long synonym list is a second vocabulary to maintain and it goes wrong quietly. This covers
     the cases where the shop's own label cannot be reached by typing the obvious thing —
     "barbecue" finding nothing while four BBQ rubs sat on the shelf is the one that started it.
     Everything this does not know falls through to the whole-shop search offered on an empty
     result, which runs on Shopify's index and knows more than a list here ever will. */
  const SYNONYMS = {
    barbecue: ['bbq'], barbeque: ['bbq'], bbq: ['barbecue'],
    chilli: ['chili', 'chile'], chili: ['chile', 'chilli'], chile: ['chili', 'chilli'],
    tisane: ['herbal'], decaf: ['caffeine free'], decaffeinated: ['caffeine free'],
    peppers: ['pepper'], herbs: ['herb'], spices: ['spice'],
  };
  const hasWord = (text, w) =>
    text.includes(w) || (SYNONYMS[w] || []).some((alt) => text.includes(alt));

  function matches(c) {
    if (c.guest) return false;
    if (S.scope === 'w') { if (!c.f.jo.length) return false; }
    else if (S.scope !== 'all' && c.kind !== S.scope) return false;
    if (S.q && !S.q.toLowerCase().split(/\s+/).every((w) => hasWord(c.text, w))) return false;
    for (const f of FACETS) if (!hit(c, f)) return false;
    return true;
  }
  /* whether a card passes one facet: anything, when nothing in it is on */
  const hit = (c, f) => !S.f[f.k].size || [...S.f[f.k]].some((v) => c.f[f.k].includes(v));
  /* Which page of the current results a product falls on — null if the active filters exclude it,
     in which case the rail still opens it and the grid is left where it was. */
  function pageFor(handle) {
    const i = CARDS.filter(matches).findIndex((c) => c.h === handle);
    return i < 0 ? null : Math.floor(i / PER) + 1;
  }
  const activeCount = () => FACETS.reduce((n, f) => n + S.f[f.k].size, 0);
  const cartCount = () => cart.items.reduce((n, l) => n + l.quantity, 0);
  const inCart = () => new Set(cart.items.map((l) => l.handle));
  /* the tab that hangs off the top of a card once it is in the basket */
  function paintCartTabs() {
    const qty = new Map();
    cart.items.forEach((l) => qty.set(l.handle, (qty.get(l.handle) || 0) + l.quantity));
    CARDS.forEach((c) => {
      const n = qty.get(c.h) || 0;
      c.el.classList.toggle('has-cart', n > 0);
      const tab = c.el.querySelector('.in-cart-tab');
      if (!tab) return;
      tab.style.display = n > 0 ? 'flex' : 'none';
      if (n > 0) tab.textContent = n === 1 ? 'In cart' : `In cart × ${n}`;
    });
  }

  /* ---------- render: show and hide, never rebuild ---------- */
  function render() {
    let shown;
    if (S.view === 'cart') {
      const keys = inCart();
      shown = CARDS.filter((c) => keys.has(c.h));
      CARDS.forEach((c) => { c.el.hidden = !keys.has(c.h); });
    } else {
      const hits = CARDS.filter(matches);
      const pages = Math.max(1, Math.ceil(hits.length / PER));
      if (S.page > pages) S.page = pages;
      const from = (S.page - 1) * PER, to = S.page * PER;
      const visible = new Set(hits.slice(from, to).map((c) => c.h));
      CARDS.forEach((c) => { c.el.hidden = !visible.has(c.h); });
      shown = hits;
      renderFoot(pages);
    }
    CARDS.forEach((c) => c.el.classList.toggle('is-sel', c.h === S.sel));

    let empty = $('sr-empty');
    if (!shown.length) {
      if (!empty) {
        empty = document.createElement('div');
        empty.id = 'sr-empty'; empty.className = 'sr-empty';
        $('sr-grid').appendChild(empty);
      }
      /* A dead end with a way out. The showroom searches the words on the cards; Shopify's own
         index also reads descriptions and knows its own spellings, so when this search comes back
         empty on a typed query the honest next step is to hand it over rather than to imply the
         shop does not stock the thing. */
      empty.innerHTML = S.view === 'cart'
        ? '<p>Nothing in the cart yet.</p>'
        : `<p>Nothing on the shelves matches ${S.q ? `“${esc(S.q)}”` : 'those'}${activeCount() && S.q ? ' with those filters' : ''}.</p>
           <div class="sr-empty__acts">
             <button class="sr-cta" data-sr-widen type="button">Show everything</button>
             ${S.q ? `<a class="sr-cta" href="/search?q=${encodeURIComponent(S.q)}">Search the whole shop for “${esc(S.q)}”</a>` : ''}
           </div>`;
      empty.hidden = false;
    } else if (empty) empty.hidden = true;

    renderStrip(shown.length);
    renderBasket();
    if (S.view === 'cart') $('sr-foot').innerHTML = '';

    const fc = $('sr-fcount'); fc.hidden = !activeCount(); fc.textContent = activeCount();
    /* The hidden rail's tab carries the same number, and says it to a screen reader. */
    const tc = $('sr-ftab-count');
    if (tc) {
      tc.hidden = !activeCount(); tc.textContent = activeCount();
      $('sr-filters-tab').setAttribute('aria-label', 'Show filters' + (activeCount() ? ` (${activeCount()} on)` : ''));
    }
    const cc = $('sr-ccount'); cc.hidden = !cartCount(); cc.textContent = cartCount();
    document.querySelectorAll('.sr-mode button').forEach((b) =>
      b.setAttribute('aria-pressed', String(b.dataset.view === S.view)));
    paintCartTabs();
    const clear = $('sr-clear');
    clear.disabled = !activeCount();
    clear.textContent = 'Clear all' + (activeCount() ? ` (${activeCount()})` : '');
    renderRail();
    placeRail();
    paintCmd();
    paintRecents();
    /* The buy box lives in a sheet on a phone, so a cart change has to reach it there — it is not
       re-rendered by renderRail the way the rail's copy is. */
    if (sheets.checkout && sheets.checkout.isOpen()) {
      sheets.checkout.setContent('<div class="sr-sheet__scroll">' + summaryHTML() + '</div>');
      wireSummary();
    }
    writeURL();
  }

  function renderStrip(n) {
    const bits = [];
    FACETS.forEach((f) => {
      if (!S.f[f.k].size) return;
      const vals = [...S.f[f.k]].map((v) => facetLabel(f.k, v));
      bits.push(f.k === 'jo' ? 'for ' + vals.join(' or ').toLowerCase()
        : f.k === 'an' ? 'when ' + vals.join(' or ').toLowerCase()
          : f.label.toLowerCase() + ' ' + vals.join(' or '));
    });
    /* With the rail the scope is always All, so the noun comes from the one group with chips on. */
    const wall = FRAIL ? (FACETS.find((f) => S.f[f.k].size) || {}).g : null;
    const what = NOUN || (S.scope === 't' || wall === 'Tea' ? 'teas' : S.scope === 's' || wall === 'Spice' ? 'spices'
      : S.scope === 'w' ? 'herbal and wellness blends' : 'teas and spices');
    const strip = $('sr-strip');
    /* in cart mode the basket tab already says all of this */
    strip.hidden = S.view === 'cart';
    if (strip.hidden) return;
    strip.innerHTML =
      `<span><b>${n}</b> ${what}${S.q ? ` matching “${esc(S.q)}”` : ''}${bits.length ? ' · ' + bits.join(' · ') : ''}</span>`
      + ((S.q || activeCount()) ? '<button class="sr-cta" data-sr-widen type="button">Show everything</button>' : '');
  }

  /* the second hanging tab: what is in the basket, and how far from free local delivery.
     It belongs in the chrome rather than in cart mode, because the moment someone decides to add
     one more thing is while they are still browsing. */
  function renderBasket() {
    const el = $('sr-basket');
    const n = cartCount();
    if (!n) { el.hidden = true; return; }
    el.hidden = false;
    /* The full bar in miniature: the same track and the same three ticks, unlabelled at this size,
       and the next goal in words beside it. Named in full, every time: "free local delivery"
       alone has been read as free shipping. */
    const there = cart.total >= SCALE;
    el.classList.toggle('is-there', there);
    const says = cart.total < MIN_ORDER
      ? `<b>${money(MIN_ORDER - cart.total)}</b> to the ${m0(MIN_ORDER)} minimum order`
      : LOCAL_TICK && cart.total < FREE_AT
        ? `<b>${money(FREE_AT - cart.total)}</b> to free delivery in Calgary`
        : !there
          ? `<b>${money(SCALE - cart.total)}</b> to free ${FREE_SHIP ? 'shipping across Canada' : 'local delivery in Calgary'}`
          : (FREE_SHIP ? 'Free shipping across Canada' : 'Free local delivery in Calgary');
    const tick = (at) => (at > 0 && at < SCALE ? `<s style="left:${(at / SCALE) * 100}%"></s>` : '');
    el.innerHTML =
      `<span><b>${n}</b> item${n === 1 ? '' : 's'} · <b>${money(cart.total)}</b></span>`
      + `<span class="sr-bar-mini"><i style="width:${Math.min(100, (cart.total / SCALE) * 100)}%"></i>${tick(MIN_ORDER)}${LOCAL_TICK ? tick(FREE_AT) : ''}</span>`
      + `<span>${says}</span>`
      + (S.view === 'cart'
        ? '<button class="sr-cta" data-sr-widen type="button">Keep shopping</button>'
        : `<button class="sr-cta" data-sr-view="cart" type="button">${cart.total >= FREE_AT ? 'Check out' : 'See the cart'}</button>`);
  }

  /* Page by page with arrows, or straight to a number. The step buttons are the ones people
     actually use, so they carry a word as well as an arrow and sit at either end where a thumb
     lands; at the first and last page they stay in place, disabled, rather than vanishing and
     shuffling everything sideways. */
  function renderFoot(pages) {
    if (pages < 2) return ($('sr-foot').innerHTML = '');
    const want = new Set([1, pages, S.page, S.page - 1, S.page + 1]);
    const list = [...want].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
    const arrow = (d) => `<svg class="sr-pg__ico" viewBox="0 0 20 20" aria-hidden="true"><path d="${
      d < 0 ? 'm12 4-5 6 5 6' : 'm8 4 5 6-5 6'}"/></svg>`;
    const step = (n, d, word) => `<button class="sr-pg sr-pg--step" type="button" data-sr-page="${n}"
      ${n < 1 || n > pages ? 'disabled' : ''} aria-label="${word} page">${
      d < 0 ? arrow(d) + `<span>${word}</span>` : `<span>${word}</span>` + arrow(d)}</button>`;

    let out = step(S.page - 1, -1, 'Previous') + '<span class="sr-pg__nums">';
    list.forEach((n, i) => {
      if (i && n - list[i - 1] > 1) out += '<span class="sr-pg__gap" aria-hidden="true">…</span>';
      out += `<button class="sr-pg" type="button" data-sr-page="${n}"
        aria-current="${S.page === n ? 'page' : 'false'}"
        aria-label="Page ${n} of ${pages}">${n}</button>`;
    });
    out += '</span>' + step(S.page + 1, 1, 'Next');
    $('sr-foot').innerHTML = out;
  }

  /* How far the rail is pulled up to sit level with the strip beside it. MEASURED rather than
     guessed: the strip's pills wrap at narrow widths and in cart mode the strip is not there at
     all, so any constant here is wrong at some width. The gap it leaves under the bar is --sr-edge,
     the same distance the rail sits from the right edge of the browser.
     The shelf's 8px top margin is part of the sum because the lift has to cancel it too. */
  function placeRail() {
    const strip = document.querySelector('.sr-strip');
    if (!strip) return;
    const edge = parseFloat(getComputedStyle(app).getPropertyValue('--sr-edge')) || 12;
    const stripH = strip.hidden ? 0 : strip.getBoundingClientRect().height;
    app.style.setProperty('--sr-rail-lift', Math.max(0, stripH + 8 - edge) + 'px');
  }
  window.addEventListener('resize', placeRail);

  /* ---------- rail ---------- */
  let railToken = 0;
  function renderRail() {
    const rail = $('sr-rail');
    /* On a phone the rail is display:none, but "invisible" is not "absent": leaving the buy box
       rendered in here as well as in the sheet would put two elements with the same ids on the
       page, and every getElementById in wireSummary would find the hidden one. */
    if (isMobile()) { rail.innerHTML = ''; return; }
    rail.classList.toggle('is-cart', S.view === 'cart');
    if (S.view === 'cart') {
      /* summary only. The detail panel crowded the one number a person is deciding against. */
      rail.innerHTML = summaryHTML();
      wireSummary();
      return;
    }
    if (!S.sel) {
      rail.innerHTML = '<div class="sr-rail__empty">Pick anything on the left and it opens here — without losing your place in the grid.</div>';
      return;
    }
    rail.innerHTML = '<div id="sr-detail"><div class="sr-rail__empty">Opening…</div></div>';
    loadDetail($('sr-detail'));
  }

  async function loadDetail(host) {
    const token = ++railToken;
    const handle = S.sel;
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=rail`);
      if (!res.ok) throw new Error(res.status);
      const html = await res.text();
      if (token !== railToken) return;
      host.innerHTML = html;
      wireDetail(host, handle);
    } catch (e) {
      if (token !== railToken) return;
      host.innerHTML = `<div class="sr-rail__empty">Could not open that one.
        <a href="/products/${encodeURIComponent(handle)}">Open its page instead</a>.</div>`;
    }
  }

  function wireDetail(host, handle) {
    const variants = JSON.parse(host.querySelector('[data-sr-variants]')?.textContent || '[]');
    const chosen = [];
    const segs = [...host.querySelectorAll('.sr-seg')];
    /* A combination that does not exist (2026-09-24) — a merged ground/whole product sells Ground in
       80g and Whole in 70g. The same rule as the product page (sections/product-masthead.liquid): a
       click keeps the value just chosen and moves the other rows to the nearest real variant, in stock
       first; a row after the first shows ONLY the values that exist with the rows above it. Sold-out
       values still strike through; only values that do not exist are hidden. One row changes nothing. */
    const pressIn = (seg, val) => seg.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.val === val)));
    const reconcile = (i) => {
      if (variants.find((v) => v.opts.every((o, j) => chosen[j] == null || o === chosen[j]))) return;
      const pool = variants.filter((v) => v.opts[i] === chosen[i]);
      const pick = pool.find((v) => v.available) || pool[0];
      if (!pick) return;
      pick.opts.forEach((o, j) => { chosen[j] = o; if (segs[j]) pressIn(segs[j], o); });
    };
    const fence = () => segs.forEach((seg, i) => {
      if (i === 0) return;
      seg.querySelectorAll('button').forEach((b) => {
        const exists = variants.some((v) => v.opts[i] === b.dataset.val && v.opts.slice(0, i).every((o, j) => chosen[j] == null || o === chosen[j]));
        b.hidden = !exists;
        b.style.display = exists ? '' : 'none';
      });
    });
    segs.forEach((seg, i) => {
      const on = seg.querySelector('[aria-pressed="true"]') || seg.querySelector('button:not([disabled])');
      chosen[i] = on ? on.dataset.val : null;
      seg.querySelectorAll('button').forEach((b) => (b.onclick = () => {
        if (b.disabled) return;
        chosen[i] = b.dataset.val;
        pressIn(seg, b.dataset.val);
        reconcile(i);
        fence();
        sync();
      }));
    });
    fence();
    const priceEl = host.querySelector('[data-sr-price]');
    const addEl = host.querySelector('[data-sr-add]');
    const rmEl = host.querySelector('[data-sr-remove]');
    const current = () => variants.find((v) => v.opts.every((o, i) => chosen[i] == null || o === chosen[i]));
    const lineFor = (v) => (v ? cart.items.find((l) => l.id === v.id) : null);
    function sync() {
      const v = current();
      const line = lineFor(v);
      if (priceEl && v) priceEl.textContent = money(v.price / 100);
      if (addEl) {
        addEl.disabled = !v || !v.available;
        addEl.textContent = !v || !v.available ? 'Sold out' : line ? 'Add another' : 'Add to cart';
      }
      /* The way out, beside the way in — otherwise a change of mind means a trip to cart mode.
         It takes ONE off, it does not clear the line. Six bags and wanting five was previously a
         choice between removing all six and starting again, or leaving it wrong; and overshooting
         the second time put you back at the same choice. Undoing a tap should cost one tap. */
      if (rmEl) {
        rmEl.hidden = !line;
        rmEl.textContent = line && line.quantity > 1 ? 'Remove one' : 'Remove';
        rmEl.onclick = () => { if (line) changeLine(line.key, line.quantity - 1); };
      }
    }
    sync();
    /* the other views: click a thumbnail, the main image swaps */
    const main = host.querySelector('#sr-rail-img');
    host.querySelectorAll('[data-sr-shot]').forEach((b) => (b.onclick = () => {
      if (!main) return;
      main.src = b.dataset.srShot; main.removeAttribute('srcset');
      host.querySelectorAll('[data-sr-shot]').forEach((x) => x.setAttribute('aria-current', String(x === b)));
    }));
    if (addEl) addEl.onclick = async () => {
      const v = current();
      if (!v || !v.available) return;
      /* The button says what is happening too. The toast is at the foot of the screen and the eye
         is on the thing that was just pressed, so the answer has to be in both places. */
      const wore = addEl.textContent;
      addEl.disabled = true;
      addEl.textContent = 'Adding…';
      await addToCart(v.id, 1, (host.querySelector('.sr-rail__t') || {}).textContent
        ? (host.querySelector('.sr-rail__t').textContent.trim()
          + (v.title && !/^default/i.test(v.title) ? ' · ' + v.title : ''))
        : null);
      addEl.disabled = false;
      addEl.textContent = wore;
      sync();
    };
  }

  /* ---------- cart ---------- */
  async function loadCart() {
    try { const j = await (await fetch('/cart.js')).json(); cart = { items: j.items, total: j.total_price / 100 }; }
    catch (e) { /* keep what we had */ }
  }
  async function changeLine(key, quantity) {
    /* Read the line BEFORE the change: at quantity 0 it is gone from the cart by the time the
       answer comes back, and "Removed" with nothing after it is barely an acknowledgement. */
    const was = cart.items.find((l) => l.key === key);
    const name = was ? lineName(was) : 'that';
    const had = was ? was.quantity : 0;
    const down = quantity < had;
    toast((quantity === 0 ? 'Removing ' : down ? 'Taking one off ' : 'Adding one to ') + name + '…', 'working');
    try {
      const res = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }) });
      /* A refused change comes back as a 4xx with a JSON description — a checkout validation, a
         line that no longer exists, more than the shop holds. Without this check the toast said
         "Removed" over a cart that had not changed (found live 2026-09-20, when the $40 rule was
         briefly refusing cart edits). Re-read the cart either way so the drawer shows the truth. */
      if (!res.ok) {
        const why = await res.json().catch(() => ({}));
        await loadCart(); render();
        toast(why.description || why.message || 'Could not change that — try again', 'bad');
        return;
      }
      await loadCart(); render();
      /* Say how many went, not just which line. "Removed Cream of Earl Grey · 50g" when three bags
         just left is an under-report of what the tap actually did. */
      if (quantity === 0) toast(had > 1 ? `Removed all ${had} × ${name}` : `Removed ${name}`);
      else toast(`${name} · now ${quantity}`);
    } catch (e) { toast('Could not change that — try again', 'bad'); }
  }
  async function addToCart(id, quantity, label) {
    toast('Adding ' + (label || 'that') + '…', 'working');
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, quantity }) });
      /* Shopify answers a refused add — out of stock, or more than the shop holds — with a 4xx and
         a JSON description. Without this check the toast said "Added" for something that was not. */
      if (!res.ok) {
        const why = await res.json().catch(() => ({}));
        toast(why.description || why.message || 'That is not available right now', 'bad');
        return false;
      }
      await loadCart(); render();
      toast('Added ' + (label || 'to the cart'));
      return true;
    } catch (e) { toast('That did not go in — try again', 'bad'); return false; }
  }

  /* ONE bar, three ticks — the same object the cart drawer carries, so the two cannot drift.
     Two stacked bars made the reader do arithmetic across them; one track with the gates drawn on it
     says the whole thing at a glance. The Calgary label rides above the track, the other two below,
     because three labels in one row overlap on a phone.
     Red until the minimum is cleared, because below it there is no order to be had; green after,
     because from there it is a real basket and the rest is a bonus. */
  function bar(now) {
    const pct = Math.min(100, (now / SCALE) * 100);
    const tick = (at, label, up) => {
      const on = now >= at ? ' is-passed' : '';
      const left = (at / SCALE) * 100;
      return `<span class="sr-notch${on}" style="left:${left}%" aria-hidden="true"></span>`
        + `<span class="sr-notch__l${up ? ' sr-notch__l--up' : ''}${on}" style="left:${left}%">${label}</span>`;
    };
    const end = FREE_SHIP > 0 ? [FREE_SHIP, `${m0(FREE_SHIP)} free shipping`] : [FREE_AT, `${m0(FREE_AT)} free in Calgary`];
    return `<div class="sr-gate sr-gate--one${now >= MIN_ORDER ? ' is-done' : ''}">
      <div class="sr-track${LOCAL_TICK ? ' has-up' : ''}">
        <div class="sr-progress"><i style="width:${pct}%"></i></div>
        ${MIN_ORDER > 0 && MIN_ORDER < SCALE ? tick(MIN_ORDER, `${m0(MIN_ORDER)} min`, false) : ''}
        ${LOCAL_TICK ? tick(FREE_AT, `${m0(FREE_AT)} free in Calgary`, true) : ''}
        <span class="sr-end-l${now >= end[0] ? ' is-passed' : ''}">${end[1]}</span>
      </div>
      <p class="sr-hint">${toGo(now, 'strong')}</p>
    </div>`;
  }

  function summaryHTML() {
    const z = ZONES.find((x) => x.handle === S.zone) || ZONES[0] || { label: 'Shipping', price: 0, regions: '' };
    /* It used to test /calgary/ against the handle alone — 'local-delivery' — so Calgary never took
       the under-$60 branch and was quoted Free at any size. zoneCents knows the zone. */
    /* An empty cart ships nothing: no postage in its total. */
    const ship = cart.items.length ? zoneCents(z) / 100 : 0;
    const canCheckout = cart.items.length && cart.total >= MIN_ORDER;
    const zoneLabel = (x) => `${x.label} \u00b7 ${zoneCents(x) ? money(zoneCents(x) / 100) : 'Free'}`;
    return `<div class="sr-buy"><div class="sr-buy__scroll"><div class="sr-rail__summary">
      <h3>Your order</h3>
      <div class="sr-lines">${cart.items.map((l) => `
        <div class="sr-line">
          <span class="sr-line__t">${esc(l.product_title)}</span>
          <span class="sr-line__v">${esc(l.variant_title || 'One size')} \u00b7 ${money(l.final_line_price / 100)}</span>
          <div class="sr-qty" data-sr-line="${l.key}">
            <button type="button" data-d="-1" aria-label="One fewer">\u2212</button>
            <span>${l.quantity}</span>
            <button type="button" data-d="1" aria-label="One more">+</button>
          </div>
        </div>`).join('')}</div>
      <div class="sr-sumrow"><span>Subtotal (${cartCount()})</span><span>${money(cart.total)}</span></div>
      <div class="sr-opt">
        <span class="sr-opt__l">Shipping to</span>
        <div class="sr-scope sr-scope--zone">
          <button class="sr-zone__face" id="sr-zone-face" type="button" aria-haspopup="listbox" aria-expanded="false">
            <span>${esc(zoneLabel(z))}</span>
            <svg class="sr-ico sr-ico--chev" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5"/></svg>
          </button>
          <div class="sr-scope__panel" id="sr-zone-panel" role="listbox" hidden>
            ${ZONES.map((x) => `<button type="button" role="option" data-zone="${x.handle}"
              aria-selected="${x.handle === S.zone}"><strong>${esc(zoneLabel(x))}</strong>
              ${x.regions ? `<span>${esc(x.regions)}</span>` : ''}</button>`).join('')}
          </div>
        </div>
      </div>
      <div class="sr-sumrow"><span>${esc(z.regions || z.label)}</span><span>${ship ? money(ship) : 'Free'}</span></div>
      <div class="sr-sumrow is-total"><span>Total</span><span>${money(cart.total + ship)}</span></div>
      ${bar(cart.total)}
    </div></div>
    <div class="sr-buy__foot">
      <form action="/cart" method="post"><button class="sr-btn" name="checkout" type="submit"
        style="width:100%" ${canCheckout ? '' : 'disabled'}>Checkout</button></form>
      ${PAY ? `<div class="sr-pay">${PAY}</div>` : ''}
    </div></div>`;
  }
  function wireSummary() {
    const face = $('sr-zone-face'), panel = $('sr-zone-panel');
    if (!face) return;
    /* IN FLOW, NOT FIXED (2026-09-20). The panel used to be position:fixed, placed from the
       button's viewport rect — and on a phone the buy box lives inside the bottom sheet, which is a
       TRANSFORMED element, so a fixed child positions itself against the sheet rather than the
       screen and viewport coordinates put it off the bottom of the phone. The cart drawer's picker
       had already solved the same problem the honest way: open in flow, push what follows down,
       let the scrolling region scroll to it. Nothing to measure, nothing to clip. On a phone the
       list is a sheet of its own instead (Dayton, 2026-09-20). */
    face.onclick = (e) => {
      e.stopPropagation();
      if (isMobile()) { openZones(); return; }
      const open = panel.hidden;
      panel.hidden = !open; face.setAttribute('aria-expanded', String(open));
      if (open) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    panel.addEventListener('mousedown', (e) => e.stopPropagation());
    document.addEventListener('mousedown', function close(e) {
      if (panel.hidden) return;
      if (panel.contains(e.target) || face.contains(e.target)) return;
      panel.hidden = true; face.setAttribute('aria-expanded', 'false');
    });
    panel.querySelectorAll('[data-zone]').forEach((b) => (b.onclick = () => {
      S.zone = b.dataset.zone;
      try { localStorage.setItem('sr-zone', S.zone); } catch (_) {}
      render();
    }));
  }

  /* ---------- filters panel ---------- */
  /* IN THE RAIL, A FACET FOLDS (2026-10-31, FMF's FilterGroups). A 224px rail cannot hold ten facets
     of chips open at once without becoming a long scroll, so each facet's name is a button. Which
     start open (Dayton, the same day: Tea's Kind "open by default, and then the other character and
     caffeine can be present but closed"): the first of each group — Kind, Kind, What's going on? — and
     any with a chip on; the rest folded, a folded facet showing how many of its chips are on. "More
     precisely" stays open: it is the second half of the question above it. What someone opens or folds
     themselves is kept until the page reloads — the panel is rebuilt on every chip press, so the choice
     lives here, not in the DOM.
     In the sheet, and on every shelf without the rail, nothing folds: the panel is as it was. */
  const OPEN = new Map();
  function facetOpen(f, first) {
    if (OPEN.has(f.k)) return OPEN.get(f.k);
    if (S.f[f.k].size) return true;
    return first || f.k === 'an';
  }
  /* AND THE GROUPS FOLD, ONE OPEN AT A TIME (Dayton, 2026-10-31: "clicking spice or wellness opens up their
     subcategories … This way a user can see all the sections in the default view. Also, opening tea or
     spice or wellness, closes the other two main categories so only one can be open at once"). Which is
     open: the one someone opened (or none, if they folded it); else the first with a chip on; else Tea.
     Pressing a chip in one keeps it open. With the rail the groups never set a scope (there is no
     picker; matches() reads the walls), so all three stay on screen whatever is picked. On a shelf
     with only one group, it does not fold.
     PICK: null until someone opens or folds a group; '' once they have folded the open one. */
  let PICK = null;
  function openGroup(shown) {
    if (PICK === '' || shown.includes(PICK)) return PICK;
    const on = shown.find((g) => FACETS.some((f) => f.g === g && S.f[f.k].size));
    return on || shown[0];
  }
  function buildGroups() {
    /* Whether the question was offered. "More precisely" is an answer to it, and a follow-up to a
       question nobody was asked is a prompt to press a chip that does not exist. */
    let asked = false;
    const inRail = FRAIL && !!$('sr-overlay').closest('#sr-filters');
    /* ONLY THE WALL BEING BROWSED (Dayton, 2026-09-27: "if a user is specifically browsing only tea or only
       spice … then only those filters should be viewable"). All Tea & Spice shows the three groups; Tea,
       Spices and Herbal & Wellness each show their own. The scope is the bar's picker, or the group a chip
       was pressed in. Not with the rail (Dayton, 2026-10-31: picking Green must not make the Spice and
       Wellness filters disappear): there the scope is always All and the three groups always show. */
    const SHOW = { all: ['Tea', 'Spice', 'Wellness'], t: ['Tea'], s: ['Spice'], w: ['Wellness'] }[S.scope] || ['Tea', 'Spice', 'Wellness'];
    const built = GROUPS.filter((g) => SHOW.includes(g.name)).map((g) => {
      let first = true;
      const rows = FACETS.filter((f) => f.g === g.name && (!ONLY.length || ONLY.includes(f.k))).map((f) => {
        const counts = new Map();
        SHELF.forEach((c) => c.f[f.k].forEach((v) => counts.set(v, (counts.get(v) || 0) + 1)));
        let vals = [...counts];
        /* A FILTER WHOSE ONLY OUTCOME IS THE SAME GRID is the twin of one whose only outcome is an
           empty grid, and hides for the same reason: one value, carried by every card, so pressing
           it changes nothing. On a page of black teas that is "Kind: Black 28" over 28 cards. */
        if (f.k !== 'an' && vals.length === 1 && vals[0][1] === SHELF.length) return '';
        if (f.k === 'an' && !asked) return '';
        if (f.k === 'jo' || f.k === 'an' || f.k === 'st') {
          /* The shop's order, not stock's. These two are a written sequence — the questions run
             from the commonest complaint to the long game, and the answers under each run in the
             order the filter bar lists them. Sorting by how many products happen to match puts
             "Long-term support" above "I can't sleep" because there are more of one on the shelf
             today, which is not a reason to ask someone about it first. The spice shelves too run
             in the order Dayton named them (ST above). */
          vals.sort((a, b) => order(f.k, a[0]) - order(f.k, b[0]));
        } else vals.sort((a, b) => b[1] - a[1]);
        if (f.k === 'an') {
          if (!S.f.jo.size) return `<div class="sr-facet"><div class="sr-facet__l">${f.label} — pick what’s going on first.</div></div>`;
          vals = vals.filter(([v]) => S.f.jo.has(v.split('|')[0]));
        } else if (f.k !== 'jo') {
          /* The open-ended facets are capped — dishes and flavours run to thirty and the panel is
             not a catalogue. The questions are NOT: there are thirteen and a cap of twelve was
             quietly dropping the last one off the end. */
          vals = vals.slice(0, 12);
        }
        /* A CONTROL WITH NOTHING TO MATCH HIDES ITSELF. The counts above are taken from the cards
           actually in the grid, so on the full Showroom every facet has values and this never
           fires — but a collection shelf renders the same panel over ONE collection's cards, where
           most dimensions are simply not about it. Offering "Cuisine" on a page of black teas is a
           filter whose only possible outcome is an empty grid.
           The `an` facet is exempt: it returns its own "pick what's going on first" prompt above,
           which is a real instruction rather than an empty control. */
        if (!vals.length) return '';
        if (f.k === 'jo') asked = true;
        /* LIVE COUNTS with the rail (Dayton, 2026-10-31: "if I select green and there are only 3 sweet teas in
           that subcategory, then the sweet filter should say '3', not 55"). A chip's number is what pressing it
           would show: the cards that pass every OTHER facet of its group that is on, and carry it. Its own facet
           is left out because a press there swaps rather than adds; the other groups because a press here
           clears them; and What's going on? leaves out More precisely, which a new question clears. Order, cap
           and the hide rules above stay on the shelf-wide counts, so the tiles do not shuffle as you pick. A
           chip at 0 keeps its place, dimmed and not pressable — unless it is the one on, so it can come off. */
        let live = null;
        if (FRAIL) {
          live = new Map();
          const others = FACETS.filter((o) => o.g === f.g && o.k !== f.k && !(f.k === 'jo' && o.k === 'an'));
          SHELF.forEach((c) => {
            if (others.every((o) => hit(c, o))) c.f[f.k].forEach((v) => live.set(v, (live.get(v) || 0) + 1));
          });
        }
        /* The label in its own span: in the rail a chip is a tile whose label wraps (two or three lines,
           a break allowed after a slash — "Thai/<wbr>Southeast Asian"); as a pill it reads as before. */
        const chips = `<div class="sr-chiprow">${vals.map(([v, all]) => {
          const label = esc(facetLabel(f.k, v));
          const on = S.f[f.k].has(v);
          const n = live ? live.get(v) || 0 : all;
          return `<button class="sr-chip" type="button" data-k="${f.k}" data-v="${esc(v)}" aria-pressed="${on}"${
            !n && !on ? ' disabled' : ''}${inRail ? ` title="${label}"` : ''}><span class="sr-chip__l">${label.replace(/\//g, '/<wbr>')}</span><span class="n">${n}</span></button>`;
        }).join('')}</div>`;
        if (!inRail) return `<div class="sr-facet"><div class="sr-facet__l">${f.label}</div>${chips}</div>`;
        const open = facetOpen(f, first);
        first = false;
        const on = S.f[f.k].size;
        return `<div class="sr-facet${open ? ' is-open' : ''}" data-k="${f.k}"><h3 class="sr-facet__h"><button class="sr-facet__t" type="button" aria-expanded="${open}" aria-controls="sr-fb-${f.k}"><span class="sr-facet__l">${f.label}</span>${
          on ? `<span class="sr-count" aria-label="${on} on">${on}</span>` : ''}<svg class="sr-ico sr-ico--chev" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5"/></svg></button></h3><div class="sr-facet__b" id="sr-fb-${f.k}">${chips}</div></div>`;
      }).join('');
      return { g, rows };
    /* And a GROUP whose every facet hid itself is a heading over nothing. */
    }).filter((x) => x.rows.trim());
    const fold = inRail && built.length > 1;
    const openG = fold ? openGroup(built.map((x) => x.g.name)) : '';
    $('sr-groups').innerHTML = built.map(({ g, rows }) => {
      /* The name alone: the hint ("what to drink") came off on 2026-09-27. */
      if (!fold) return `<section class="sr-grp" data-scope="${g.scope}"><div class="sr-grp__h">${g.name}</div>${rows}</section>`;
      const open = g.name === openG;
      const on = FACETS.reduce((n, f) => n + (f.g === g.name ? S.f[f.k].size : 0), 0);
      const id = 'sr-gb-' + g.name.toLowerCase();
      return `<section class="sr-grp is-fold${open ? ' is-open' : ''}" data-scope="${g.scope}" data-g="${g.name}"><h2 class="sr-grp__h"><button class="sr-grp__t" type="button" aria-expanded="${open}" aria-controls="${id}"><span class="sr-grp__l">${g.name}</span>${
        on ? `<span class="sr-count" aria-label="${on} on">${on}</span>` : ''}<svg class="sr-ico sr-ico--chev" viewBox="0 0 20 20" aria-hidden="true"><path d="m5 8 5 5 5-5"/></svg></button></h2><div class="sr-grp__b" id="${id}">${rows}</div></section>`;
    }).join('');

    $('sr-groups').querySelectorAll('.sr-grp').forEach((sec) => sec.addEventListener('click', (e) => {
      /* Opening or folding a facet or a group is looking, not choosing: it does not set the scope. */
      if (e.target.closest('.sr-facet__t, .sr-grp__t')) return;
      const sc = sec.dataset.scope;             /* using a group says what you are browsing */
      if (!FRAIL && sc && S.scope !== sc) { S.scope = sc; paintScope(); }
      if (sec.dataset.g) PICK = sec.dataset.g;  /* and the group you used stays the open one */
    }, true));
    $('sr-groups').querySelectorAll('.sr-grp__t').forEach((t) => (t.onclick = () => {
      const sec = t.closest('.sr-grp');
      const open = !sec.classList.contains('is-open');
      PICK = open ? sec.dataset.g : '';
      $('sr-groups').querySelectorAll('.sr-grp.is-fold').forEach((s) => {
        const mine = s === sec && open;
        s.classList.toggle('is-open', mine);
        s.querySelector('.sr-grp__t').setAttribute('aria-expanded', String(mine));
      });
    }));
    $('sr-groups').querySelectorAll('.sr-facet__t').forEach((t) => (t.onclick = () => {
      const row = t.closest('.sr-facet');
      const open = !row.classList.contains('is-open');
      row.classList.toggle('is-open', open);
      t.setAttribute('aria-expanded', String(open));
      OPEN.set(row.dataset.k, open);
    }));
    $('sr-groups').querySelectorAll('.sr-chip').forEach((c) => (c.onclick = () => {
      const set = S.f[c.dataset.k];
      /* ONE CHIP A FACET with the rail (Dayton, 2026-10-31: "If I select green, it should filter greens.
         If I then select black, green should be unselected … If I select black and then select sweet, they
         can both be selected"). Pressing another chip in the same facet swaps to it; pressing the one
         that is on turns it off. Facets of the same group still combine — but ONE GROUP AT A TIME (the
         same day: "If I select baking or anything in spice or wellness when I have a tea filter selected,
         it should de-select the tea filter. And vice versa"), so a chip clears the other two groups'.
         Elsewhere chips add up, as they always have. */
      if (set.has(c.dataset.v)) set.delete(c.dataset.v);
      else {
        if (FRAIL) {
          const g = FACETS.find((o) => o.k === c.dataset.k).g;
          FACETS.forEach((o) => { if (o.g !== g) S.f[o.k].clear(); });
          set.clear();
        }
        set.add(c.dataset.v);
      }
      /* The facet key is the UI's own — st cu di pr he tt ch en jo an — so a report reads as the
         chip row does. `on` says whether this tap added or removed the value. */
      track('shelf_filter', { facet: c.dataset.k, value: c.dataset.v, on: set.has(c.dataset.v) });
      if (c.dataset.k === 'jo') [...S.f.an].forEach((a) => { if (!S.f.jo.has(a.split('|')[0])) S.f.an.delete(a); });
      S.page = 1; S.view = 'grid';
      buildGroups(); render();
    }));
  }

  /* ---------- odds and ends ---------- */
  /* ---------- toasts ----------
     Every cart action is a round trip to Shopify, and on a phone that is long enough that a tap
     with no answer reads as a broken button — people tap again, and buy two.
     So a toast goes up the INSTANT the tap lands, saying what is being done, and is rewritten in
     place when the server answers. Never removed and re-added: replacing the node restarts the
     animation and the thing flickers at exactly the moment it is meant to reassure.

     `sr` on the element is load-bearing. The toast is appended to document.body, outside #sr-app,
     and the showroom's tokens are declared on `.sr` — so the old toast resolved --sr-bar and
     --sr-bar-ink to nothing and painted transparent with inherited ink. It has been firing on
     every add since it was written, and has never once been visible. */
  let toastTimer;
  let toastEl;
  function toast(msg, state) {
    if (!toastEl || !toastEl.isConnected) {
      toastEl = document.createElement('div');
      toastEl.className = 'sr sr-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
      /* Next frame, so the element exists un-shown for one paint and the transition has a
         from-state to run from. */
      requestAnimationFrame(() => toastEl && toastEl.classList.add('is-up'));
    }
    toastEl.textContent = msg;
    toastEl.classList.toggle('is-working', state === 'working');
    toastEl.classList.toggle('is-bad', state === 'bad');
    clearTimeout(toastTimer);
    /* A "working" toast has no timer: it is replaced by the outcome, or by the failure. */
    if (state === 'working') return;
    toastTimer = setTimeout(hideToast, state === 'bad' ? 4000 : 2400);
  }
  function hideToast() {
    if (!toastEl) return;
    const el = toastEl;
    toastEl = null;
    el.classList.remove('is-up');
    setTimeout(() => el.remove(), 260);
  }
  /* "Cream of Earl Grey · 100g", where the product has sizes, and just the name where it does not
     — a toast that only says "Added" is no use to someone who added two things quickly. */
  function lineName(l) {
    const v = l.variant_title && !/^default/i.test(l.variant_title) ? ' · ' + l.variant_title : '';
    return (l.product_title || '') + v;
  }
  function widen() {
    if (S.view === 'cart') { S.view = 'grid'; return render(); }
    S.q = ''; $('sr-q').value = ''; S.scope = 'all'; PICK = null; paintScope(); paintQClear();
    FACETS.forEach((f) => S.f[f.k].clear());
    S.page = 1; buildGroups(); render();
  }

  /* Switching to the cart. Below the rail's edge there IS no rail, so the buy box has nowhere to
     render — the sheet is where it lives. Above the phone's edge the bottom bar is not there to
     offer a Checkout tab either, so without this the band between the two edges could reach cart
     view and find the numbers nowhere at all. */
  function setView(v) {
    if (v !== S.view) track('cart_view_toggle', { view: v, via: 'segment' });
    S.view = v;
    render();
    if (v === 'cart' && isMobile()) { closeSheets('checkout'); openCheckout(); }
    else if (v !== 'cart' && sheets.checkout) sheets.checkout.close();
  }

  document.addEventListener('click', (e) => {
    /* Sheets are portalled to document.body so they escape every stacking context on the page —
       which also puts them outside #sr-app, so this guard has to admit them explicitly. Without
       that, every control inside a sheet (the steppers, Add to cart, the shipping picker, a
       recents row) is inert, and inertly: no error, nothing happens. */
    if (!app.contains(e.target) && !e.target.closest('.sr-sheet')) return;
    if (e.target.closest('a')) return;            /* Full page, and the rail's links, still navigate */
    const open = e.target.closest('[data-sr-open]');
    if (open) {
      S.sel = open.dataset.srOpen;
      S.selIsDefault = false;
      /* A USER opened this — the URL restore and the default opening product above are not
         opens and are deliberately not counted, or every page load would look like interest. */
      track('rail_open', { handle: S.sel });
      remember(S.sel);
      /* On a phone the product arrives as a sheet, because there is no rail to put it in. A row in
         Recently viewed is a door like any other, so it lands here too and opens the same sheet —
         which is the point of keeping recents a door rather than a saved list. */
      if (isMobile()) {
        closeSheets('product');
        openProduct(S.sel);
        return render();
      }
      /* Details asks to read a product, and the rail cannot show a product and a basket at once.
         So step back out to the grid — and turn to the page the product actually sits on, rather
         than leaving someone on page 1 reading about something they cannot see. */
      if (S.view !== 'cart') return render();
      S.view = 'grid';
      const p = pageFor(S.sel);
      if (p) S.page = p;
      render();
      /* The grid has just been rebuilt around a different page — put the card the person asked
         about where they can see it, or they are reading the rail with no idea where it came from. */
      const card = BY[S.sel];
      if (card) card.el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    const page = e.target.closest('[data-sr-page]');
    if (page) { S.page = +page.dataset.srPage; render(); return window.scrollTo({ top: app.offsetTop, behavior: 'smooth' }); }
    if (e.target.closest('[data-sr-widen]')) return widen();
    const step = e.target.closest('.sr-qty[data-sr-line] button');
    if (step) {
      const box = step.closest('[data-sr-line]');
      const now = +box.querySelector('span').textContent;
      return changeLine(box.dataset.srLine, Math.max(0, now + +step.dataset.d));
    }
    const toView = e.target.closest('[data-sr-view]');
    if (toView) return setView(toView.dataset.srView);
  });
  /* the card's own quantity buttons belong to showroom-v3.js — just keep our cart in step */
  document.addEventListener('click', (e) => {
    if (e.target.closest('.qty-btn, .size-toggle-btn')) setTimeout(() => loadCart().then(() => {
      const cc = $('sr-ccount'); cc.hidden = !cartCount(); cc.textContent = cartCount();
      if (S.view === 'cart') render();
    }), 600);
  });

  /* the scope control: the face states the answer, the panel changes it (FMF's ShowroomScopeControl) */
  const SCOPE_LABEL = { all: 'All Tea & Spice', t: 'Tea', s: 'Spices', w: 'Herbal & Wellness' };
  const scopeFace = $('sr-scope'), scopePanel = $('sr-scope-panel');
  /* What the scope selector wears where there is no room for its words. Drawn to the bar's own
     icon vocabulary — 20x20, stroked, not filled — so it sits beside the search glass rather than
     looking borrowed from somewhere else. */
  const SCOPE_ICO = {
    all: '<circle cx="7" cy="7" r="2.1"/><circle cx="13" cy="7" r="2.1"/><circle cx="7" cy="13" r="2.1"/><circle cx="13" cy="13" r="2.1"/>',
    t: '<path d="M4 8h9v4.2A3.8 3.8 0 0 1 9.2 16H7.8A3.8 3.8 0 0 1 4 12.2Z"/><path d="M13 9.2h1.6a2 2 0 0 1 0 4H13"/><path d="M6.8 3.2c0 1-.9 1.3-.9 2.3M9.6 3.2c0 1-.9 1.3-.9 2.3"/>',
    s: '<path d="M7 8.4h6v7.1a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 7 15.5Z"/><path d="M7.6 8.4V5.6A1.6 1.6 0 0 1 9.2 4h1.6a1.6 1.6 0 0 1 1.6 1.6v2.8"/><path d="M9.3 11.4h.01M11 13.1h.01M9.5 14.4h.01"/>',
    w: '<path d="M3.6 16.2c0-6 4.6-9.6 12-9.6 0 6.8-4 10.6-9.6 10.6-1.6 0-2.4-.4-2.4-1Z"/><path d="M5.8 14.4c2.8-1.2 4.8-3.2 6-5.4"/>',
  };

  /* With the rail there is no picker to paint, open or close (showroom-shell.liquid leaves it out). */
  function paintScope() {
    if (!scopeFace) return;
    $('sr-scope-label').textContent = SCOPE_LABEL[S.scope] || SCOPE_LABEL.all;
    const ico = $('sr-scope-ico');
    if (ico) {
      ico.innerHTML = '<svg class="sr-ico" viewBox="0 0 20 20" aria-hidden="true">'
        + (SCOPE_ICO[S.scope] || SCOPE_ICO.all) + '</svg>';
    }
    scopePanel.querySelectorAll('[data-scope]').forEach((b) =>
      b.setAttribute('aria-selected', String(b.dataset.scope === S.scope)));
  }
  function openScope(open) {
    if (!scopeFace) return;
    scopePanel.hidden = !open;
    scopeFace.setAttribute('aria-expanded', String(open));
  }
  if (scopeFace) {
    scopeFace.onclick = (e) => { e.stopPropagation(); const want = scopePanel.hidden; closeDrops(); openScope(want); };
    scopePanel.querySelectorAll('[data-scope]').forEach((b) => (b.onclick = () => {
      S.scope = b.dataset.scope; S.page = 1; S.view = 'grid';
      openScope(false); paintScope(); buildGroups(); render();
    }));
    document.addEventListener('mousedown', (e) => {
      if (!scopePanel.hidden && !scopePanel.contains(e.target) && !scopeFace.contains(e.target)) openScope(false);
    });
  }
  /* The field's clear. Shown whenever there is text to clear — typed or already searched — because
     an empty-looking control beside a full field is the confusing half of this pattern. */
  const qClear = $('sr-clear-q');
  function paintQClear() { if (qClear) qClear.hidden = !$('sr-q').value.length && !S.q; }
  if (qClear) {
    qClear.onclick = () => {
      $('sr-q').value = '';
      S.q = ''; S.page = 1;
      paintQClear();
      render();
      $('sr-q').focus();
    };
  }
  $('sr-q').addEventListener('input', paintQClear);
  $('sr-q').addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { $('sr-q').value = ''; S.q = ''; S.page = 1; paintQClear(); return render(); }
    if (e.key === 'Enter') {
      e.preventDefault();
      S.q = e.target.value.trim(); S.page = 1; S.view = 'grid';
      paintQClear(); render();
      /* On a phone the grid is under the keyboard and the bar; the same hits arrive as a sheet
         (Dayton, 2026-09-20), each row opening its product the way a Recent row does. */
      if (isMobile() && S.q) { e.target.blur(); openResults(); }
    }
  });
  document.querySelectorAll('.sr-mode button').forEach((b) => (b.onclick = () => setView(b.dataset.view)));
  /* one dropdown idiom, shared by scope, filters and the menu — except where the filters are a rail:
     a rail is not a dropdown, so a click elsewhere must not hide it, and the bar's Filters button
     (shown only below 1200px there) opens the sheet instead. */
  const DROPS = [...(FRAIL ? [] : [['sr-filters-open', 'sr-overlay']]), ['sr-menu-open', 'sr-menu-panel'],
    ['sr-recents-open', 'sr-recents-panel']];
  function closeDrops(except) {
    DROPS.forEach(([b, p]) => {
      if (p === except) return;
      $(p).hidden = true; $(b).setAttribute('aria-expanded', 'false');
    });
  }
  DROPS.forEach(([b, p]) => {
    $(b).onclick = (e) => {
      e.stopPropagation();
      /* On a phone the Recent pill lives in the top bar (far right, beside the field — Dayton,
         2026-09-20) and opens the SHEET, not the desktop's dropdown: a 340px panel hanging off a
         64px bar is the wrong surface under a thumb. */
      if (b === 'sr-recents-open' && isMobile()) { closeDrops(); openScope(false); openRecents(); return; }
      const open = $(p).hidden;
      closeDrops(); openScope(false);
      $(p).hidden = !open; $(b).setAttribute('aria-expanded', String(open));
    };
    $(p).addEventListener('mousedown', (e) => e.stopPropagation());
  });
  const ov = $('sr-overlay');
  $('sr-filters-close').onclick = () => closeDrops();
  if (FRAIL) {
    $('sr-filters-open').onclick = (e) => { e.stopPropagation(); closeDrops(); openScope(false); openFilters(); };

    /* HIDE folds the rail up into the bar and leaves the Filters tab hanging under the plate; the tab
       unfolds it (FMF's fold, "like it is growing from the top bar"). Remembered on this device — the
       inline script in the shell applies it before the shelf is laid out. Focus follows the control
       that replaces the one pressed, so a keyboard is never left on a button that has gone. */
    const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
    const FOLD_MS = 260;
    const setFrailHidden = (hide) => {
      try { localStorage.setItem(FRAIL_HIDDEN_KEY, hide ? '1' : '0'); } catch (_) {}
      const quick = REDUCED.matches;
      if (hide) {
        app.classList.add('is-frail-folding');
        setTimeout(() => {
          app.classList.add('is-frail-hidden');
          app.classList.remove('is-frail-folding');
          placeRail();
          $('sr-filters-tab').focus();
        }, quick ? 0 : FOLD_MS);
      } else {
        app.classList.add('is-frail-folding');
        app.classList.remove('is-frail-hidden');
        void $('sr-filters').offsetWidth; /* the folded state paints once, so the unfold runs */
        app.classList.remove('is-frail-folding');
        placeRail();
        $('sr-filters-hide').focus();
      }
    };
    $('sr-filters-hide').onclick = () => setFrailHidden(true);
    $('sr-filters-tab').onclick = () => setFrailHidden(false);
  }
  document.addEventListener('mousedown', () => closeDrops());
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') ov.removeAttribute('open'); });
  $('sr-clear').onclick = () => { FACETS.forEach((f) => S.f[f.k].clear()); S.page = 1; buildGroups(); render(); };

  /* ================= the phone's chrome =========================================================
     Five sheets, all of them the one primitive in showroom-sheet.js. Nothing below duplicates a
     surface the desktop already has: the filters panel and the buy box are the SAME NODES, moved
     into a sheet and moved back, so there is one set of controls carrying one set of state. The
     alternative — rendering a second copy for the phone — is two things to keep in step, and they
     do not stay in step.
  ============================================================================================== */
  const sheets = {};
  /* Where a borrowed node came from, so it can be put back exactly where it was rather than
     appended to the end of its old parent. */
  const homes = new WeakMap();
  function adopt(node, host) {
    if (!node || !host) return;
    if (!homes.has(node)) homes.set(node, { parent: node.parentNode, next: node.nextSibling });
    host.appendChild(node);
  }
  function giveBack(node) {
    const h = node && homes.get(node);
    if (h && h.parent) h.parent.insertBefore(node, h.next);
  }

  function sheet(name, make) {
    if (!sheets[name]) sheets[name] = make();
    return sheets[name];
  }
  const closeSheets = (except) => Object.keys(sheets).forEach((k) => { if (k !== except) sheets[k].close(); });

  /* ---- Collections ---- */
  function collectionsSheet() {
    return sheet('collections', () => {
      const s = window.SRSheet.create({ ariaLabel: 'Collections' });
      const tpl = $('sr-tpl-collections');
      s.setContent('<div class="sr-sheet__head"><h2>Collections</h2></div>'
        + '<div class="sr-sheet__scroll"></div>');
      if (tpl) s.body.querySelector('.sr-sheet__scroll').appendChild(tpl.content.cloneNode(true));
      return s;
    });
  }

  /* ---- Filters ----
     Borrows the desktop panel. Clear all comes with it into the foot, because the panel's own
     header is hidden in a sheet and leaving Clear behind would strand the only way out of a
     narrowed shelf. The rail's credit strip (#sr-credit, only where the rail is) comes too, and
     goes UNDER the foot — the sheet's last row, as it is the rail's. */
  function filtersSheet() {
    return sheet('filters', () => {
      const s = window.SRSheet.create({
        ariaLabel: 'Filters',
        /* Back to the rail, where facets fold again — so the panel is redrawn for where it is now. */
        onClose: () => { giveBack($('sr-overlay')); giveBack($('sr-clear')); giveBack($('sr-credit')); if (FRAIL) buildGroups(); },
      });
      s.setContent('<div class="sr-sheet__head"><h2>Filters</h2></div>'
        + '<div class="sr-sheet__scroll" id="sr-msheet-filters"></div>'
        + '<div class="sr-sheet__foot" id="sr-msheet-ffoot"></div>');
      return s;
    });
  }
  function openFilters() {
    const s = filtersSheet();
    const ovl = $('sr-overlay');
    ovl.hidden = false;
    adopt(ovl, $('sr-msheet-filters'));
    adopt($('sr-clear'), $('sr-msheet-ffoot'));
    adopt($('sr-credit'), s.body); /* appended, so after the foot; absent off the rail, and adopt shrugs */
    if (FRAIL) buildGroups(); /* out of the rail, nothing folds */
    s.open();
  }

  /* ---- Checkout ----
     The buy box, whole: the lines, the shipping picker, both gates, the payment marks. Generated
     rather than borrowed, because the rail renders nothing on a phone — so there is only ever one
     of it on the page and its ids stay unique. */
  function checkoutSheet() {
    return sheet('checkout', () => window.SRSheet.create({ ariaLabel: 'Your order' }));
  }
  function openCheckout() {
    const s = checkoutSheet();
    s.setContent('<div class="sr-sheet__scroll">' + summaryHTML() + '</div>');
    wireSummary();
    s.open();
  }

  /* ---- Recently viewed ----
     Permanent tab, with an empty state, unlike the desktop pill which does not render at all when
     there is nothing in it. A bottom bar that grows a tab reflows every target under a thumb that
     is already moving; a top bar pill appearing moves nothing a mouse is aiming at. */
  function recentsSheet() {
    return sheet('recents', () => {
      const s = window.SRSheet.create({ ariaLabel: 'Recently viewed' });
      s.setContent('<div class="sr-sheet__head"><h2>Recently viewed</h2></div>'
        + '<div class="sr-sheet__scroll" id="sr-msheet-recents"></div>');
      return s;
    });
  }
  function openRecents() {
    const s = recentsSheet();
    $('sr-msheet-recents').innerHTML = recentsHTML();
    s.open();
  }

  /* ---- Where it ships, as a sheet ----
     The zone list, on a phone: the buy box is already a sheet, and a list that opens inside a
     sheet is best opened as the next sheet up — the primitive stacks them and Esc closes the top
     one only. Picking a zone re-renders the buy box beneath and closes this. */
  function zonesSheet() {
    return sheet('zones', () => {
      const s = window.SRSheet.create({ ariaLabel: 'Shipping to' });
      s.setContent('<div class="sr-sheet__head"><h2>Shipping to</h2></div>'
        + '<div class="sr-sheet__scroll" id="sr-msheet-zones"></div>');
      s.body.addEventListener('click', (e) => {
        const b = e.target.closest('[data-zone]');
        if (!b) return;
        S.zone = b.dataset.zone;
        try { localStorage.setItem('sr-zone', S.zone); } catch (_) {}
        render();
        s.close();
      });
      return s;
    });
  }
  function openZones() {
    const s = zonesSheet();
    $('sr-msheet-zones').innerHTML = ZONES.map((x) => `
      <button class="sr-sheet__row sr-zone__row" type="button" data-zone="${esc(x.handle)}" aria-pressed="${x.handle === S.zone}">
        <strong>${esc(x.label)} · ${zoneCents(x) ? money(zoneCents(x) / 100) : 'Free'}</strong>
        ${x.regions ? `<span>${esc(x.regions)}</span>` : ''}
      </button>`).join('');
    s.open();
  }

  /* ---- Search results, as a sheet ----
     The hits the grid already shows, listed the way Recent lists its rows — picture, name, line,
     price — so a phone gets the answer without scrolling under the keyboard for it. */
  function resultsSheet() {
    return sheet('results', () => {
      const s = window.SRSheet.create({ ariaLabel: 'Search results' });
      s.setContent('<div class="sr-sheet__head"><h2 id="sr-msheet-rtitle"></h2></div>'
        + '<div class="sr-sheet__scroll" id="sr-msheet-results"></div>');
      return s;
    });
  }
  function openResults() {
    const s = resultsSheet();
    const hits = CARDS.filter(matches);
    $('sr-msheet-rtitle').textContent = hits.length
      ? `${hits.length} for “${S.q}”`
      : `Nothing for “${S.q}”`;
    $('sr-msheet-results').innerHTML = hits.length
      ? rowsHTML(hits)
      : '<p class="sr-sheet__empty">Nothing on the shelves matches. Try fewer words, or search the whole shop from the front page.</p>';
    closeSheets('results');
    s.open();
  }

  /* Read off the cards that are already on the page rather than out of the store, so a renamed or
     repriced product cannot show yesterday's figure here. */
  function recentsHTML() {
    const list = recentCards();
    if (!list.length) {
      return '<p class="sr-sheet__empty">'
        + '<svg class="sr-cmd__ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 106 5.3L3 8"/><path d="M12 7v5l4 2"/></svg>'
        + 'Teas and spices you open will show up here.</p>';
    }
    return rowsHTML(list);
  }
  /* One row shape for Recent and for search results, so the two cannot drift. */
  function rowsHTML(list) {
    return list.map((c) => {
      const img = c.el.querySelector('img');
      /* The card's real title element. An earlier guess at `h3` matched nothing, and the fallback
         printed the handle — "cream-of-earl-grey" where the product's name should be. */
      const t = c.el.querySelector('.product-card-header-title');
      const sub = c.el.querySelector('.product-card-subtitle');
      /* firstChild, not textContent: a product on sale puts the compare-at price and a divider
         inside the same badge, and all three would come out as one run-on string. */
      const badge = c.el.querySelector('.price-badge');
      const price = badge ? (badge.firstChild?.textContent || badge.textContent || '') : '';
      const name = (t ? t.textContent : c.h).trim();
      const line = (sub ? sub.textContent.trim() : '') || (c.kind === 't' ? 'Tea' : 'Spice');
      return `<button class="sr-recent" type="button" data-sr-open="${c.h}">
        ${img ? `<img src="${img.currentSrc || img.src}" alt="" loading="lazy">` : ''}
        <span class="sr-recent__t"><b>${esc(name)}</b><span>${esc(line)}</span></span>
        <span class="sr-recent__p">${esc(price.trim())}</span>
      </button>`;
    }).join('');
  }

  /* ---- The product, where the rail would have held it ---- */
  let sheetSel = null;
  function productSheet() {
    return sheet('product', () => window.SRSheet.create({
      ariaLabel: 'Product',
      onClose: () => { sheetSel = null; },
    }));
  }
  function openProduct(handle) {
    const s = productSheet();
    if (sheetSel !== handle) {
      sheetSel = handle;
      /* `sr-rail` on the inner box, and it is not decoration. The detail fragment is styled by
         rules scoped under .sr-rail — the product name and every h4 heading among them — so
         without the class those inherit the body ink and come out black on charcoal. The class
         brings the rail's box with it; showroom-mobile.css undoes that part. */
      s.setContent('<div class="sr-sheet__scroll"><div class="sr-rail" id="sr-detail-m">'
        + '<div class="sr-rail__empty">Opening…</div></div></div>');
      loadDetail($('sr-detail-m'));
    }
    s.open();
  }

  /* ---- The desktop pill ---- */
  function paintRecents() {
    const wrap = $('sr-recents-wrap');
    if (!wrap) return;
    const list = recentCards();
    wrap.hidden = list.length === 0;          /* earned: no rows, no pill */
    if (!list.length) return;
    $('sr-recents-panel').innerHTML = recentsHTML();
    if (sheets.recents && sheets.recents.isOpen()) $('sr-msheet-recents').innerHTML = recentsHTML();
  }

  /* ---- The bar itself ---- */
  function paintCmd() {
    const bar = $('sr-cmd');
    if (!bar) return;
    const n = cartCount();
    const fc = activeCount();
    const fb = $('sr-cmd-fcount'); fb.hidden = !fc; fb.textContent = fc;
    const cb = $('sr-cmd-ccount'); cb.hidden = !n; cb.textContent = n;
    /* The mode tab shows where a tap LANDS you, not where you are. */
    const toCart = S.view === 'grid';
    $('sr-cmd-mode-label').textContent = toCart ? 'Cart' : 'Grid';
    /* ONE icon, the swap: two arrows chasing each other round a circle. The tab is a switch between
       two views, and the label beneath it already names the destination; an icon that changed with
       the label read as two different tabs taking turns (Dayton, 2026-09-20). `toCart` still
       decides the word. */
    $('sr-cmd-mode-ico').innerHTML =
      '<svg viewBox="0 0 24 24" class="sr-cmd__ico" aria-hidden="true"><path d="M4 12a8 8 0 0 1 13.7-5.7L20 8"/><path d="M20 4v4h-4"/><path d="M20 12a8 8 0 0 1-13.7 5.7L4 16"/><path d="M4 20v-4h4"/></svg>';
    void toCart;
    $('sr-cmd-mode').classList.toggle('is-on', S.view === 'cart');
    bar.querySelector('[data-sr-sheet="filters"]').classList.toggle('is-on', fc > 0);
    /* Checkout lights up only when it would actually work. A painted button that refuses the tap
       is worse than a quiet one. */
    bar.querySelector('.sr-cmd__tab--go').classList.toggle('is-ready', n > 0 && cart.total >= MIN_ORDER);
  }

  /* Tabs. The sheet tabs are mutually exclusive — opening one closes whatever else was up, so the
     phone never ends up with two stacked surfaces and an ambiguous back gesture. */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('#sr-cmd [data-sr-sheet]');
    if (tab) {
      const which = tab.dataset.srSheet;
      const already = sheets[which] && sheets[which].isOpen();
      closeSheets();
      if (already) return;
      if (which === 'collections') collectionsSheet().open();
      if (which === 'filters') openFilters();
      if (which === 'recents') openRecents();
      if (which === 'checkout') openCheckout();
      return;
    }
    if (e.target.closest('#sr-cmd-mode')) {
      closeSheets();
      /* The bar has its own Checkout tab, so flipping to the cart here shows the filtered grid and
         waits — it does not also shove the buy box in front of what was just asked for. */
      S.view = S.view === 'cart' ? 'grid' : 'cart';
      /* The phone's own toggle bypasses setView, so it reports itself — and says so. */
      track('cart_view_toggle', { view: S.view, via: 'command-bar' });
      render();
    }
  });

  /* Crossing the edge mid-session — a rotation, or a desktop window dragged narrow. Whichever
     side we land on owns the detail, so hand it over rather than leaving it on both or neither. */
  MOBILE.addEventListener('change', () => {
    closeSheets();
    sheetSel = null;
    render();
  });
  /* The rail's own edge: widened past 1200px with the filters sheet open, the panel goes home to
     the rail rather than staying in a sheet beside it. */
  if (FRAIL) WIDE.addEventListener('change', () => { closeSheets(); placeRail(); });

  /* ---------- boot ---------- */
  (async () => {
    /* A stale link carrying a reserved param lands on a page Liquid rendered empty (?page=2) or on
       the wrong template entirely (?view=cart). Strip them and reload rather than show nothing. */
    const bad = new URLSearchParams(location.search);
    if (bad.has('view') || bad.has('page')) {
      bad.delete('view'); bad.delete('page');
      const qs = bad.toString();
      location.replace(location.pathname + (qs ? '?' + qs : ''));
      return;
    }
    readURL();
    $('sr-q').value = S.q;
    paintScope();
    try { S.zone = localStorage.getItem('sr-zone') || null; } catch (_) {}
    try {
      const raw = document.getElementById('shippingZoneData');
      if (raw) { ZONES = JSON.parse(raw.textContent).zones || []; if (!S.zone) S.zone = (ZONES.find((z) => z.isDefault) || ZONES[0] || {}).handle; }
    } catch (_) {}
    await loadCart();
    buildGroups();
    render();
  })();
})();
